import type { CardDefinition } from "../domain/types/index.js";
import { DISRUPTION_CARDS } from "./disruptionCards.js";
import { MEDIUM_PROGRESS_CARDS } from "./mediumProgressCards.js";
import { OVERTIME_CARDS } from "./overtimeCards.js";
import { POOL_EXPANSION_CARDS } from "./poolExpansionCards.js";

export const CARDS = {
  ...DISRUPTION_CARDS,
  intern: {
    id: "intern", name: "新卒", type: "worker", progress: 1,
    text: "終了時に進捗+1", rewardTier: "starter",
  },
  idle: {
    id: "idle", name: "窓際", type: "worker", progress: 0,
    text: "終了時に進捗+0", rewardTier: "starter",
  },
  mid: {
    id: "mid", name: "中堅", type: "worker", progress: 2,
    text: "終了時に進捗+2", rewardTier: "small",
  },
  senior: {
    id: "senior", name: "ベテラン", type: "worker", progress: 3,
    text: "終了時に進捗+3", rewardTier: "large",
  },
  "occupational-physician": {
    id: "occupational-physician", name: "産業医", type: "worker", progress: 1,
    heal: 1, text: "終了時に進捗+1。疲労-1", rewardTier: "small",
  },
  "ai-adoption": {
    id: "ai-adoption", name: "AI導入", type: "action", investment: "progress",
    text: "このラウンドから、PJ終了まで毎ラウンド進捗+1", rewardTier: "medium",
    effect: { kind: "investment", cardId: "ai-adoption" },
  },
  "advisor-contract": {
    id: "advisor-contract", name: "顧問契約", type: "action", investment: "draw",
    text: "このラウンドから、PJ終了まで毎ラウンド1枚引く", rewardTier: "large",
    effect: { kind: "investment", cardId: "advisor-contract" },
  },
  "crisis-management-office": {
    id: "crisis-management-office", name: "危機管理室の設置", type: "action", investment: "block",
    text: "このラウンドから、PJ終了まで毎ラウンド防御+1", rewardTier: "small",
    effect: { kind: "investment", cardId: "crisis-management-office" },
  },
  "deadline-negotiation": {
    id: "deadline-negotiation", name: "納期交渉", type: "action",
    text: "このフェーズの労働者の進捗を0にする。工期+1", rewardTier: "medium",
    effect: { kind: "deadlineNegotiation" },
  },
  hedge: {
    id: "hedge", name: "リスクヘッジ", type: "action",
    text: "進捗+1。防御+1", rewardTier: "small",
    effect: {
      kind: "compound",
      effects: [{ kind: "progress", amount: 1 }, { kind: "block", amount: 1 }],
    },
  },
  "emergency-reassignment": {
    id: "emergency-reassignment", name: "緊急配置転換", type: "action",
    text: "手札1枚を捨て、2枚引く。行動+1", rewardTier: "small",
    effect: { kind: "workerCycle", draw: 2, actions: 1 },
  },
  cellar: {
    id: "cellar", name: "優先順位付け", type: "action",
    text: "行動+1。任意枚数を捨て、同数引く", rewardTier: "small",
    effect: { kind: "cellar", actions: 1 },
  },
  "knowledge-share": {
    id: "knowledge-share", name: "共有ナレッジ", type: "action",
    text: "1枚引く。行動+1。捨て札1枚を山札の上に置く", rewardTier: "small",
    effect: {
      kind: "compound",
      effects: [
        { kind: "draw", count: 1 },
        { kind: "drawAction", draw: 0, actions: 1 },
        { kind: "knowledgeShare" },
      ],
    },
  },
  "field-judgment": {
    id: "field-judgment", name: "現場判断", type: "action",
    text: "進捗+2。山札上を公開し、施策なら任意で即時使用", rewardTier: "small",
    effect: {
      kind: "compound",
      effects: [{ kind: "progress", amount: 2 }, { kind: "fieldJudgment" }],
    },
  },
  village: {
    id: "village", name: "業務フロー整備", type: "action",
    text: "1枚引く。行動+2", rewardTier: "small",
    effect: { kind: "drawAction", draw: 1, actions: 2 },
  },
  "business-recovery": {
    id: "business-recovery", name: "業務復旧", type: "action",
    text: "手札が5枚になるまで引く。行動+1", rewardTier: "medium",
    effect: { kind: "drawToHandSize", size: 5, actions: 1 },
  },
  smithy: {
    id: "smithy", name: "業務分担", type: "action",
    text: "3枚引く", rewardTier: "medium",
    effect: { kind: "draw", count: 3 },
  },
  pair: {
    id: "pair", name: "ペアプロ", type: "action",
    text: "2枚引く。進捗+1", rewardTier: "medium",
    effect: {
      kind: "compound",
      effects: [{ kind: "draw", count: 2 }, { kind: "progress", amount: 1 }],
    },
  },
  standup: {
    id: "standup", name: "朝会", type: "action",
    text: "1枚引く。行動+1。進捗+1", rewardTier: "medium",
    effect: {
      kind: "compound",
      effects: [
        { kind: "draw", count: 1 },
        { kind: "drawAction", draw: 0, actions: 1 },
        { kind: "progress", amount: 1 },
      ],
    },
  },
  meeting: {
    id: "meeting", name: "経営会議", type: "action",
    text: "手札の施策1枚を選び、2回使用する", rewardTier: "medium",
    effect: { kind: "meeting" },
  },
  policy: {
    id: "policy", name: "全社方針", type: "action",
    text: "4枚引く。防御+1", rewardTier: "large",
    effect: {
      kind: "compound",
      effects: [{ kind: "draw", count: 4 }, { kind: "block", amount: 1 }],
    },
  },
  welfare: {
    id: "welfare", name: "福利厚生", type: "action",
    text: "行動+2。進捗+2。防御+1", rewardTier: "large",
    effect: {
      kind: "compound",
      effects: [
        { kind: "drawAction", draw: 0, actions: 2 },
        { kind: "progress", amount: 2 },
        { kind: "block", amount: 1 },
      ],
    },
  },
  lab: {
    id: "lab", name: "計画会議", type: "action",
    text: "2枚引く。行動+1", rewardTier: "large",
    effect: {
      kind: "compound",
      effects: [{ kind: "draw", count: 2 }, { kind: "drawAction", draw: 0, actions: 1 }],
    },
  },
  focus: {
    id: "focus", name: "集中作業", type: "action",
    text: "手札が7枚になるまで引く。引いた施策は任意で脇へ置く", rewardTier: "large",
    effect: { kind: "focus" },
  },
  mobilize: {
    id: "mobilize", name: "総動員", type: "action",
    text: "進捗+4", rewardTier: "large",
    effect: { kind: "progress", amount: 4 },
  },
  improvement: {
    id: "improvement", name: "業務改善", type: "action",
    text: "1枚引く。行動+1。進捗+1。防御+1", rewardTier: "large",
    effect: {
      kind: "compound",
      effects: [
        { kind: "draw", count: 1 },
        { kind: "drawAction", draw: 0, actions: 1 },
        { kind: "progress", amount: 1 },
        { kind: "block", amount: 1 },
      ],
    },
  },
  "paid-leave": {
    id: "paid-leave", name: "有給休暇", type: "action",
    text: "行動+1。疲労-2", rewardTier: "small",
    effect: {
      kind: "compound",
      effects: [{ kind: "drawAction", draw: 0, actions: 1 }, { kind: "heal", amount: 2 }],
    },
  },
  sendoff: {
    id: "sendoff", name: "肩たたき", type: "action",
    text: "手札の労働者1枚を廃棄する", rewardTier: "small",
    effect: { kind: "sendoff" },
  },
  quality: {
    id: "quality", name: "品質管理", type: "action",
    text: "1枚引く。行動+1。上2枚を廃棄／捨て札／山札へ戻す", rewardTier: "medium",
    effect: {
      kind: "compound",
      effects: [
        { kind: "draw", count: 1 },
        { kind: "drawAction", draw: 0, actions: 1 },
        { kind: "quality" },
      ],
    },
  },
  training: {
    id: "training", name: "研修", type: "action",
    text: "手札の新卒または中堅1枚を1ランクアップさせる", rewardTier: "large",
    effect: { kind: "training" },
  },
  "collaboration-meeting": {
    id: "collaboration-meeting", name: "連携会議", type: "action",
    text: "1枚引く。行動+1。施策を選択し、それを2回使う", rewardTier: "large",
    effect: {
      kind: "compound",
      effects: [{ kind: "drawAction", draw: 1, actions: 1 }, { kind: "meeting", times: 2 }],
    },
  },
  "mid-career-hire": {
    id: "mid-career-hire", name: "中途採用", type: "action",
    text: "中堅労働者1枚を捨て札に加える。疲労+1", rewardTier: "medium",
    effect: { kind: "compound", effects: [{ kind: "gainWorker", cardId: "mid" }, { kind: "fatigue", amount: 1 }] },
  },
  "mass-graduate-hiring": {
    id: "mass-graduate-hiring", name: "新卒一括採用", type: "action",
    text: "手札に新卒3枚を加える", rewardTier: "small",
    effect: { kind: "gainWorkersToHand", cardId: "intern", count: 3 },
  },
  "placement-plan": {
    id: "placement-plan", name: "配属計画", type: "action",
    text: "1枚引く。行動+1。使用後の手札が新卒と窓際だけなら、進捗+1、疲労-1", rewardTier: "small",
    effect: { kind: "placementPlan" },
  },
  "new-hire-onboarding": {
    id: "new-hire-onboarding", name: "新人オンボーディング", type: "action",
    text: "新卒以外を引くまで1枚ずつ引く。行動+1", rewardTier: "medium",
    effect: { kind: "onboardInterns", actions: 1 },
  },
  "work-standardization": {
    id: "work-standardization", name: "業務標準化", type: "action",
    text: "手札の新卒1枚につき進捗+1。使用後の手札が新卒だけなら、さらに進捗+3", rewardTier: "medium",
    effect: { kind: "workStandardization" },
  },
  "spot-assignment": {
    id: "spot-assignment", name: "スポット起用", type: "action",
    text: "1枚引く。行動+1。手札に窓際があるなら、進捗+2", rewardTier: "small",
    effect: { kind: "spotAssignment" },
  },
  "psychological-counseling": {
    id: "psychological-counseling", name: "心理カウンセリング", type: "action",
    text: "手札の窓際1枚を廃棄し、ベテラン1枚を捨て札に加える", rewardTier: "medium",
    effect: { kind: "psychologicalCounseling" },
  },
  "special-task-team": {
    id: "special-task-team", name: "特命チーム編成", type: "action",
    text: "手札の窓際1枚を捨てる。2枚引く。行動+2。進捗+2", rewardTier: "medium",
    effect: { kind: "specialTaskTeam" },
  },
  headhunting: {
    id: "headhunting", name: "引き抜き採用", type: "action",
    text: "ベテラン1枚を捨て札に加える。窓際1枚を捨て札に加える", rewardTier: "medium",
    effect: { kind: "compound", effects: [{ kind: "gainWorker", cardId: "senior" }, { kind: "gainWorker", cardId: "idle" }] },
  },
  ...OVERTIME_CARDS,
  ...MEDIUM_PROGRESS_CARDS,
  ...POOL_EXPANSION_CARDS,
} as const satisfies Record<string, CardDefinition>;

export function lookupCard(cardId: string): CardDefinition | undefined {
  return CARDS[cardId as keyof typeof CARDS];
}
